import { supabase } from './supabase'
import { capitalizarTexto } from './texto'

// Obtener todos los voluntarios
export const getVoluntarios = async () => {
  const { data, error } = await supabase
    .from('voluntarios')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

// Obtener el rol del usuario actual
export const getCurrentUserRol = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .from('voluntarios')
    .select('rol, activo')
    .eq('auth_id', user.id)
    .single()

  if (error) return null
  return data?.rol
}

// Obtener un voluntario por ID
export const getVoluntarioById = async (id) => {
  const { data, error } = await supabase
    .from('voluntarios')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

// Crear un nuevo voluntario
export const crearVoluntario = async (data) => {
  try {
    const nombre = capitalizarTexto(data.nombre)
    const zona = data.zona_operativa ? capitalizarTexto(data.zona_operativa) : ''
    // 1. Crear usuario en auth.users con signUp
    // Esto configura correctamente raw_app_meta_data (provider: email, providers: ["email"])
    // y auto-confirma el email si "Confirm email" esta desactivado en Supabase
    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: {
          nombre,
          rol: data.rol || 'voluntario'
        }
      }
    });

    if (signUpError) throw signUpError;

    if (!authData?.user?.id) {
      throw new Error('No se pudo crear el usuario de autenticacion');
    }

    // 2. Verificar que raw_app_meta_data tenga los providers correctos
    const userMeta = authData.user;
    const appMeta = userMeta.app_metadata || {};
    
    if (!appMeta.providers || !appMeta.providers.includes('email')) {
      // Intentar actualizar metadatos via RPC si es necesario
      try {
        const { error: updateMetaError } = await supabase.rpc('asegurar_metadatos_auth', {
          p_user_id: authData.user.id
        });
        if (updateMetaError) {
          console.warn('No se pudieron actualizar metadatos (no critico):', updateMetaError);
        }
      } catch (metaErr) {
        // No critico, continuar
        console.warn('Error actualizando metadatos:', metaErr);
      }
    }

    // 3. Esperar un momento para que Supabase propague el usuario
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 4. Insertar en tabla voluntarios usando la funcion RPC
    const { data: result, error: rpcError } = await supabase
      .rpc('crear_voluntario', {
        p_auth_id: authData.user.id,
        p_email: data.email,
        p_nombre: nombre,
        p_telefono: data.telefono || '',
        p_cedula: data.cedula || '',
        p_zona_operativa: zona,
        p_rol: data.rol || 'voluntario'
      });

    if (rpcError) throw rpcError;

    if (!result.success) {
      // Si falla la insercion en voluntarios, eliminar el usuario de auth
      try {
        await supabase.rpc('eliminar_usuario_auth', { p_user_id: authData.user.id });
      } catch (cleanupErr) {
        console.error('Error al limpiar usuario de auth:', cleanupErr);
      }
      throw new Error(result.error || 'Error al crear el perfil del voluntario');
    }

    return { ...result, auth_id: authData.user.id };
  } catch (error) {
    console.error('Error en crearVoluntario:', error);
    throw error;
  }
};

// Actualizar un voluntario (usa RPC con SECURITY DEFINER para evitar "User not allowed")
export const updateVoluntario = async (id, updates) => {
  const { data, error } = await supabase
    .rpc('actualizar_voluntario', {
      p_voluntario_id: id,
      p_nombre: updates.nombre || null,
      p_telefono: updates.telefono || null,
      p_cedula: updates.cedula || null,
      p_zona_operativa: updates.zona_operativa || null,
      p_rol: updates.rol || null,
      p_vehiculo: updates.vehiculo !== undefined ? updates.vehiculo : null,
      p_password: updates.password || null
    })

  if (error) throw error
  if (!data.success) throw new Error(data.error || 'Error al actualizar voluntario')
  return data
}

// Cambiar rol de voluntario (usa RPC para bypassear RLS)
export const updateVoluntarioRol = async (id, rol) => {
  const { data, error } = await supabase
    .rpc('cambiar_rol_voluntario', {
      p_voluntario_id: id,
      p_nuevo_rol: rol
    })

  if (error) throw error
  if (!data.success) throw new Error(data.error || 'Error al cambiar rol')
  return data
}

// Activar/Desactivar voluntario (usa RPC para bypassear RLS)
export const toggleVoluntarioActivo = async (id, activo) => {
  const { data, error } = await supabase
    .rpc('toggle_activo_voluntario', {
      p_voluntario_id: id,
      p_activo: activo
    })

  if (error) throw error
  if (!data.success) throw new Error(data.error || 'Error al cambiar estado')
  return data
}

// Eliminar voluntario (soft delete) (usa RPC para bypassear RLS)
export const deleteVoluntario = async (id) => {
  const { data, error } = await supabase
    .rpc('eliminar_voluntario', {
      p_voluntario_id: id
    })

  if (error) throw error
  if (!data.success) throw new Error(data.error || 'Error al eliminar voluntario')
  return data
}

// Buscar voluntarios
export const searchVoluntarios = async (termino) => {
  const { data, error } = await supabase
    .from('voluntarios')
    .select('*')
    .or(`nombre.ilike.%${termino}%,email.ilike.%${termino}%,cedula.ilike.%${termino}%`)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}